import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  LayoutDashboard, Calendar, Scissors, Settings, LogOut, Menu, X,
  DollarSign, Users, Clock, TrendingUp, Plus, Edit, Trash2, Check
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const BACKEND_URL = "https://zenvy-backend-e0zx.onrender.com";
const API = `${BACKEND_URL}/api`;

export default function OwnerDashboard() {
  const { user, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salon, setSalon] = useState(null);
  
  // Service form state
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [serviceForm, setServiceForm] = useState({ name: '', description: '', price: '', duration: '' });
  const [services, setServices] = useState([]);
  const [editingService, setEditingService] = useState(null);

  useEffect(() => {
    fetchDashboard();
    fetchBookings();
  }, []);
  useEffect(()=>{
    if(salon?.salon_id){
      fetchServices();
    }
  },[salon]);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API}/owner/dashboard`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
        setSalon(data.salon);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await fetch(`${API}/bookings`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  };

  const fetchServices = async () => {
    try {
      if (!salon?.salon_id) return;

      const response = await fetch(`${API}/salons/${salon.salon_id}/services`, {
        headers: getAuthHeader(),
      });

      const data = await response.json();
      console.log("SERVICES DATA:", data);

      if (response.ok) {
        setServices(Array.isArray(data) ? data : []);
      } else {
        setServices([]);
      }
      
    } catch (error){
      console.error("Failed to fetch services:",error);
      setServices([]);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      const response = await fetch(`${API}/bookings/${bookingId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        toast.success('Booking status updated');
        fetchBookings();
        fetchDashboard();
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    if (!salon) return;

    try {
      const url = editingService 
        ? `${API}/salons/${salon.salon_id}/services/${editingService.service_id}`
        : `${API}/salons/${salon.salon_id}/services`;
      
      const response = await fetch(url, {
        method: editingService ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({
          name: serviceForm.name,
          description: serviceForm.description,
          price: parseFloat(serviceForm.price),
          duration: parseInt(serviceForm.duration),
          is_active: true
        })
      });

      if (response.ok) {
        toast.success(editingService ? 'Service updated' : 'Service added');
        setShowServiceDialog(false);
        setServiceForm({ name: '', description: '', price: '', duration: '' });
        setEditingService(null);
        fetchDashboard();
      } else {
        toast.error('Failed to save service');
      }
    } catch (error) {
      toast.error('Failed to save service');
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!salon || !window.confirm('Delete this service?')) return;

    try {
      const response = await fetch(`${API}/salons/${salon.salon_id}/services/${serviceId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      if (response.ok) {
        toast.success('Service deleted');
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-800',
      confirmed: 'bg-blue-100 text-blue-800',
      in_service: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-zinc-100 text-zinc-800';
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menuItems = [
    { id: 'overview', icon: LayoutDashboard, label: 'Overview' },
    { id: 'bookings', icon: Calendar, label: 'Bookings' },
    { id: 'services', icon: Scissors, label: 'Services' },
    { id: 'settings', icon: Settings, label: 'Settings' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-zinc-900"></div>
      </div>
    );
  }

  if (!salon) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Scissors className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-zinc-900 mb-2">No Salon Yet</h2>
            <p className="text-zinc-600 mb-6">Create your salon profile to start receiving bookings.</p>
            <Button onClick={() => window.location.href = '/owner/create-salon'}>
  Create Salon
</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      {/* Sidebar */}
      <aside className={`dashboard-sidebar bg-white border-r border-zinc-200 ${sidebarOpen ? 'open' : ''}`}>
        <div className="p-6 border-b border-zinc-200">
          <Link to="/" className="text-2xl font-bold text-zinc-900">Zenvy</Link>
          <p className="text-sm text-zinc-500 mt-1">Shop Owner</p>
        </div>

        <nav className="p-4 space-y-1">
          {menuItems.map(item => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                activeTab === item.id 
                  ? 'bg-zinc-900 text-white' 
                  : 'text-zinc-600 hover:bg-zinc-100'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-zinc-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Top Bar */}
        <header className="bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between sticky top-0 z-40">
          <div className="flex items-center gap-4">
            <button 
              className="lg:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <h1 className="text-xl font-semibold text-zinc-900">{salon.name}</h1>
            {salon.is_verified && (
              <Badge className="bg-green-100 text-green-800">Verified</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-500 hidden sm:block">{user?.email}</span>
          </div>
        </header>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6" data-testid="overview-tab">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-zinc-500">Total Bookings</p>
                        <p className="text-3xl font-bold text-zinc-900">{dashboardData?.total_bookings || 0}</p>
                      </div>
                      <Calendar className="w-10 h-10 text-blue-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-zinc-500">Pending</p>
                        <p className="text-3xl font-bold text-amber-600">{dashboardData?.pending_bookings || 0}</p>
                      </div>
                      <Clock className="w-10 h-10 text-amber-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-zinc-500">Completed</p>
                        <p className="text-3xl font-bold text-green-600">{dashboardData?.completed_bookings || 0}</p>
                      </div>
                      <Check className="w-10 h-10 text-green-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-zinc-500">Total Revenue</p>
                        <p className="text-3xl font-bold text-zinc-900">${dashboardData?.total_revenue || 0}</p>
                      </div>
                      <DollarSign className="w-10 h-10 text-zinc-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Today's Bookings */}
              <Card>
                <CardHeader>
                  <CardTitle>Today's Bookings</CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboardData?.todays_bookings?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboardData.todays_bookings.map(booking => (
                        <div key={booking.booking_id} className="flex items-center justify-between p-4 bg-zinc-50 rounded-lg">
                          <div>
                            <p className="font-medium">{booking.customer_name}</p>
                            <p className="text-sm text-zinc-500">{booking.service_name} at {booking.time_slot}</p>
                          </div>
                          <Badge className={getStatusBadge(booking.status)}>{booking.status}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-center py-8">No bookings scheduled for today</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Bookings Tab */}
          {activeTab === 'bookings' && (
            <Card data-testid="bookings-tab">
              <CardHeader>
                <CardTitle>All Bookings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="dashboard-table w-full">
                    <thead>
                      <tr className="text-zinc-500">
                        <th>Customer</th>
                        <th>Service</th>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map(booking => (
                        <tr key={booking.booking_id} className="border-t border-zinc-100">
                          <td>
                            <p className="font-medium">{booking.customer_name}</p>
                            <p className="text-sm text-zinc-500">{booking.customer_phone}</p>
                          </td>
                          <td>{booking.service_name}</td>
                          <td>{booking.booking_date}</td>
                          <td>{booking.time_slot}</td>
                          <td>
                            <Badge className={getStatusBadge(booking.status)}>{booking.status}</Badge>
                          </td>
                          <td>
                            <Select
                              value={booking.status}
                              onValueChange={(value) => updateBookingStatus(booking.booking_id, value)}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="in_service">In Service</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {bookings.length === 0 && (
                    <p className="text-zinc-500 text-center py-8">No bookings yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Services Tab */}
          {activeTab === 'services' && (
            <Card data-testid="services-tab">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Services</CardTitle>
                <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
                  <DialogTrigger asChild>
                    <Button onClick={() => { setEditingService(null); setServiceForm({ name: '', description: '', price: '', duration: '' }); }}>
                      <Plus className="w-4 h-4 mr-2" /> Add Service
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingService ? 'Edit Service' : 'Add New Service'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddService} className="space-y-4">
                      <div>
                        <Label>Service Name</Label>
                        <Input
                          value={serviceForm.name}
                          onChange={(e) => setServiceForm({...serviceForm, name: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={serviceForm.description}
                          onChange={(e) => setServiceForm({...serviceForm, description: e.target.value})}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Price ($)</Label>
                          <Input
                            type="number"
                            value={serviceForm.price}
                            onChange={(e) => setServiceForm({...serviceForm, price: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <Label>Duration (min)</Label>
                          <Input
                            type="number"
                            value={serviceForm.duration}
                            onChange={(e) => setServiceForm({...serviceForm, duration: e.target.value})}
                            required
                          />
                        </div>
                      </div>
                      <Button type="submit" className="w-full">
                        {editingService ? 'Update Service' : 'Add Service'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  {services?.map(service => (
                    <div key={service.service_id} className="flex items-center justify-between p-4 bg-zinc-50 rounded-lg">
                      <div>
                        <p className="font-medium">{service.name}</p>
                        <p className="text-sm text-zinc-500">{service.description}</p>
                        <p className="text-sm text-zinc-500">{service.duration} min</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <p className="text-xl font-bold">${service.price}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingService(service);
                            setServiceForm({
                              name: service.name,
                              description: service.description || '',
                              price: service.price.toString(),
                              duration: service.duration.toString()
                            });
                            setShowServiceDialog(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600"
                          onClick={() => handleDeleteService(service.service_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {services.length === 0 && (
                    <p className="text-zinc-500 text-center py-8">No services added yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <Card data-testid="settings-tab">
              <CardHeader>
                <CardTitle>Salon Settings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Salon Name</Label>
                      <Input value={salon.name} disabled className="bg-zinc-50" />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Input value={salon.category === 'men' ? "Men's Salon" : "Women's Salon"} disabled className="bg-zinc-50" />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input value={salon.phone} disabled className="bg-zinc-50" />
                    </div>
                    <div>
                      <Label>City</Label>
                      <Input value={salon.city} disabled className="bg-zinc-50" />
                    </div>
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Input value={salon.address} disabled className="bg-zinc-50" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea value={salon.description || ''} disabled className="bg-zinc-50" />
                  </div>
                  <p className="text-sm text-zinc-500">
                    Contact support to update your salon details.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
